import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { NotifierContextProvider } from "react-headless-notifier";
import { createBrowserRouter, RouterProvider, useParams } from "react-router-dom";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import ReactDOM from "react-dom/client";

import "./i18n";

import AuthenticatedLayout from "./layouts/AuthenticatedLayout";
import PublicLayout from "./layouts/PublicLayout";
import KioskLayout from "./layouts/KioskLayout";
import Account from "./pages/Accounts/Account";
import AccountList from "./pages/Accounts/AccountsList";
import AccountDuplicates from "./pages/Accounts/AccountDuplicates";
import AdminMenu from "./pages/Admin/AdminMenu";
import AwaitingInventory from "./pages/AwaitingInventory/AwaitingInventory";
import PartsVehicleLookup from "./pages/AwaitingInventory/PartsVehicleLookup";
import Calendar from "./pages/Calendar/Calendar";
import OldTripManager from "./pages/Calendar/OldTripManager";
import TripManager from "./pages/Calendar/TripManager";
import Chat from "./pages/Chat/Chat";
import CategoryEditor from "./pages/CategoryEditor/CategoryEditor";
import VehicleComplexity from "./pages/VehicleComplexity/VehicleComplexity";
import EditCategory from "./pages/CategoryEditor/EditCategory";
import Contact from "./pages/Contacts/Contact";
import ContactList from "./pages/Contacts/ContactList";
import DashboardHome from "./pages/Dashboard/DashboardHome";
import DriverLead from "./pages/Driver/DriverLead";
import FindLead from "./pages/Driver/FindLead";
import DriverTracking from "./pages/DriverTracking/DriverTracking";
import BulkList from "./pages/EbayListings/BulkList";
import EbayListings from "./pages/EbayListings/EbayListings";
import EbayOptimisation from "./pages/EbayOptimisation/EbayOptimisation";
import EbayUnlisted from "./pages/EbayListings/EbayUnlisted";
import ListingRedirect from "./pages/EbayListings/ListingRedirect";
import EbayMessages from "./pages/EbayMessages/EbayMessages";
import PartProMessages from "./pages/PartProMessages/PartProMessages";
import CreatePartRequest from "./pages/PartRequests/CreatePartRequest";
import PartRequests from "./pages/PartRequests/PartRequests";
import PartProBids from "./pages/PartProBids/PartProBids";
import PartProBidVehicle from "./pages/PartProBids/PartProBidVehicle";
import ViewPartRequest from "./pages/PartRequests/ViewPartRequest";
import EbayOrders from "./pages/EbayOrders/EbayOrders";
import EbayReturns from "./pages/EbayReturns/EbayReturns";
import ErrorBoundry from "./pages/Error/ErrorBoundry";
import Exports from "./pages/Exports/Exports";
import EbayIntegration from "./pages/Intergrations/Ebay";
import TellerooIntegration from "./pages/Intergrations/Telleroo";
import ShopifyIntegration from "./pages/Intergrations/Shopify";
import PartProIntegration from "./pages/Intergrations/PartPro";
import Jobs from "./pages/Jobs/Jobs";
import Job from "./pages/Jobs/ViewJob";
import ProductionPlanner from "./pages/ProductionPlanner/ProductionPlanner";
import CreateLead from "./pages/Leads/CreateLead";
import CreateLeadMultipleVehicles from "./pages/Leads/CreateLeadMultipleVehicles";
import Lead from "./pages/Leads/Lead";
import Leads from "./pages/Leads/LeadList";
import CollectionsBoard from "./pages/Leads/CollectionsBoard";
import DeletedLeads from "./pages/Leads/DeletedLeads";
import Quotes from "./pages/Leads/Quotes";
import RequireAuth from "./pages/Login/RequireAuth";
import PartCategoryRequiredImagesManager from "./pages/PartCategoryRequiredImages/PartCategoryRequireImagesManager";
import PartsChecker from "./pages/PartsChecker/PartsChecker";
import PartsEditor from "./pages/PartsEditor/PartsEditor";
import Payments from "./pages/Payments/Payments";
import CustomerWebForm from "./pages/Public Pages/CustomerWebForm";
import Schedule from "./pages/Schedule/Schedule";
import Search from "./pages/Search/Search";
import GeneralSettings from "./pages/Settings/General";
import MailSettings from "./pages/Settings/Mail";
import Settings from "./pages/Settings/Settings";
import Stock from "./pages/Stock/Stock";
import Till from "./pages/Till/Till";
import StockQc from "./pages/Stock/StockQc";
import ViewStock from "./pages/Stock/ViewStock";
import { Test } from "./pages/Test/Test";
import CreateTicket from "./pages/Tickets/CreateTicket";
import Tickets from "./pages/Tickets/Tickets";
import ViewTicket from "./pages/Tickets/ViewTicket";
import Help from "./pages/Help/Help";
import HelpArticleRoute from "./pages/Help/HelpArticleRoute";
import { helpArticles } from "./pages/Help/helpArticles";
import Vehicle from "./pages/Vehicles/Vehicle";
import VehicleList from "./pages/Vehicles/VehicleList";
import VehicleBoard from "./pages/Vehicles/VehicleBoard";
import EditVehicle from "./pages/Vehicles/EditVehicle";
import VehiclesAwaitingArrival from "./pages/VehiclesAwaitingArrival/VehiclesAwaitingArrival";
import VehiclesCollected from "./pages/VehiclesCollected/VehiclesCollected";
import WasteTracking from "./pages/WasteTracking/WasteTracking";
import Hauliers from "./pages/WasteTracking/Hauliers";
import IFrame from "./pages/Widget/IFrame";
import * as Sentry from "@sentry/react";
import { sentryDSN, sentryReplaySampleRate, sentryReplayOnErrorSampleRate } from "./config";
import CreateVehicle from "./pages/Vehicles/CreateVehicle";

import "./index.css";
import Reports from "./pages/Reports/Reports";
import Performance from "./pages/Performance/Performance";
import StockCheckReports from "./pages/StockCheckReport/StockCheckReports";
import MechanicDamageReport from "./pages/MechanicDamage/MechanicDamageReport";
import ViewStockCheckReport from "./pages/StockCheckReport/ViewStockCheckReport";
import ViewReport from "./components/reports/ViewReport";
import InvoiceList from "./components/invoices/InvoiceList";
import Invoices from "./pages/Invoices/Invoices";
import ViewInvoice from "./pages/Invoices/ViewInvoice";
import SalesQuotes from "./pages/SalesQuotes/SalesQuotes";
import ViewSalesQuote from "./pages/SalesQuotes/ViewSalesQuote";
import RecentlyListed from "./pages/EbayListings/RecentlyListed";
import DropOff from "./pages/DropOff/DropOff";
import DepollutionList from "./pages/Depollution/DepollutionList";
import TriageList from "./pages/Triage/TriageList";
import Triage from "./pages/Triage/Triage";
import Depollution from "./pages/Depollution/Depollution";
import NotFound from "./pages/Error/NotFound";
import ProductionDashboard from "./pages/Production/ProductionDashboard";
import StationManager from "./pages/Production/StationManager";
import Profile from "./pages/Profile/Profile";
import Core from "./pages/Core/Core";
import CoreBins from "./pages/Core/CoreBins";
import WorkOrders from "./pages/WorkOrders/WorkOrders";
import ShopifySyncDebug from "./pages/Debug/ShopifySyncDebug";
import ShopifyRepairDebug from "./pages/Debug/ShopifyRepairDebug";
import ShopifyMediaSync from "./pages/ShopifyMediaSync/ShopifyMediaSync";
import ShopifyToolsDebug from "./pages/Debug/ShopifyToolsDebug";
import ShopifyImageRecompressionDebug from "./pages/Debug/ShopifyImageRecompressionDebug";
import ShopifyEbayBackfillDebug from "./pages/Debug/ShopifyEbayBackfillDebug";
import PartProEbayBackfillDebug from "./pages/Debug/PartProEbayBackfillDebug";
import HistoricalImportDebug from "./pages/Debug/HistoricalImportDebug";
import ListingRefreshDebug from "./pages/Debug/ListingRefreshDebug";
import ListingRefreshBatchDebug from "./pages/Debug/ListingRefreshBatchDebug";
import ShopifyEbayBackfill from "./pages/ShopifyEbayBackfill/ShopifyEbayBackfill";
import isLocalDebug from "./utils/isLocalDebug";
import { registerSameTabNavigate } from "./utils/sameTabNavigation";

Sentry.init({
  dsn: sentryDSN,
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  // integrations: [
  //   Sentry.replayIntegration()
  // ],
  // // Session Replay
  // replaysSessionSampleRate: sentryReplaySampleRate, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  // replaysOnErrorSampleRate: sentryReplayOnErrorSampleRate // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

const widgetPath = (url: string) => {
  return `https://app.recyclingsoftware.co.uk/${url}?embed=true`;
  // return `http://localhost:3000/${url}?embed=true`;
};

function WidgetLeadIFrame() {
  const { leadId } = useParams();
  // The lead is this customer's, so the widget dashboard must drop any impersonation left over from using it as SAV
  return <IFrame src={`${widgetPath(`leads/${encodeURIComponent(leadId ?? "")}`)}&stopImpersonating=true`} />;
}

const showDebugRoutes = isLocalDebug();

const router = createBrowserRouter(
  [
    {
      element: <PublicLayout />,
      children: [
        {
          path: "/customer-info",
          element: <CustomerWebForm />,
          errorElement: <ErrorBoundry />,
        },
        {
          path: "/integrations",
          children: [
            {
              path: "telleroo",
              element: <TellerooIntegration />,
            },
            {
              path: "ebay",
              element: <EbayIntegration />,
            },
            {
              path: "shopify",
              element: <ShopifyIntegration />,
            },
            {
              path: "partpro",
              element: <PartProIntegration />,
            },
          ],
        },
        {
          path: "*",
          element: <NotFound />
        },
      ],
    },
    {
      // Chromeless kiosk shell: the till has its own layout so the sidebar/topbar never render.
      element: <KioskLayout />,
      errorElement: <ErrorBoundry />,
      children: [
        {
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: false, isExporter: false }}
              requiredFeature="accounts"
            />
          ),
          children: [{ path: "till", element: <Till /> }],
        },
      ],
    },
    {
      path: "/",
      element: <AuthenticatedLayout />,
      errorElement: <ErrorBoundry />,
      children: [
        {
          path: "*",
          element: <NotFound />
        },
        {
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: false, isExporter: false }}
              requiredFeature="dashboard"
            />
          ),
          children: [{ index: true, element: <DashboardHome /> }],
        },
        {
          //Admin only routes
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: false, isMechanic: false, isDriver: false, isExporter: false }}
              requiredFeature="admin"
            />
          ),
          children: [
            ...(showDebugRoutes ? [
              {
                path: "debug",
                children: [
                  {
                    // Every Shopify tool lives under one hub rather than four sibling nav entries.
                    path: "shopify",
                    children: [
                      {
                        index: true,
                        element: <ShopifyToolsDebug />,
                      },
                      {
                        path: "image-sync",
                        element: <ShopifyMediaSync />,
                      },
                      {
                        path: "image-recompression",
                        element: <ShopifyImageRecompressionDebug />,
                      },
                      {
                        path: "product-sync",
                        element: <ShopifySyncDebug />,
                      },
                      {
                        path: "repair",
                        element: <ShopifyRepairDebug />,
                      },
                      {
                        path: "ebay-backfill",
                        element: <ShopifyEbayBackfillDebug />,
                      },
                    ],
                  },
                  {
                    path: "partpro-ebay-backfill",
                    element: <PartProEbayBackfillDebug />,
                  },
                  {
                    // Wilma 4 item 11: Toyota's pre-Wilma ELVs with their dismantling dates, then their
                    // V5C / WCN / COD files. Localhost only; the endpoints behind it are AdminOnly.
                    path: "historical-import",
                    element: <HistoricalImportDebug />,
                  },
                  {
                    // Onboarding tool: re-standardise and republish a customer's imported eBay
                    // listings. Localhost only; the endpoints behind it are AdminOnly.
                    path: "listing-refresh",
                    children: [
                      {
                        index: true,
                        element: <ListingRefreshDebug />,
                      },
                      {
                        path: ":batchId",
                        element: <ListingRefreshBatchDebug />,
                      },
                    ],
                  },
                ],
              },
            ] : []),
            {
              // Not under `debug`: the backlog this repairs is in production, so the page has to be
              // reachable there. The endpoints behind it are AdminOnly.
              path: "shopify-backfill",
              element: <ShopifyEbayBackfill />,
            },
            {
              // Admin-only for now: it names people. The endpoint behind it is AdminOnly too.
              path: "mechanic-damage",
              element: <MechanicDamageReport />,
            },
            {
              path: "admin",
              element: <AdminMenu />,
              children: [
                {
                  index: true,
                  element: <AdminMenu />,
                },
                {
                  path: ":section",
                  element: <AdminMenu />,
                },
              ],
            },
            {
              path: "parts-editor",
              children: [
                {
                  index: true,
                  element: <PartsEditor />,
                },
                {
                  path: ":categoryId",
                  element: <PartsEditor />,
                },
              ],
            },
            {
              path: "ebay-messages",
              children: [
                {
                  index: true,
                  element: <EbayMessages />,
                },
              ],
            },
            {
              path: "partpro-messages",
              children: [
                {
                  index: true,
                  element: <PartProMessages />,
                },
              ],
            },
            {
              path: "vehicle-complexity",
              children: [
                {
                  index: true,
                  element: <VehicleComplexity />,
                },
              ],
            },
            {
              path: "category-editor",
              children: [
                {
                  index: true,
                  element: <CategoryEditor />,
                },
                {
                  path: ":categoryId",
                  element: <EditCategory />,
                },
              ],
            },
            {
              path: "category-images",
              children: [
                {
                  index: true,
                  element: <PartCategoryRequiredImagesManager />,
                },
              ],
            },
            {
              path: "test",
              children: [
                {
                  index: true,
                  element: <Test />,
                },
              ],
            },
          ],
        },
        {
          //Reporting. These lived in the admin-only block above, where allowedRoles alone
          //denied every non-admin before RequireAuth ever looked at the feature - so moving them
          //here is what actually makes the reporting scope reachable. The scope still fails closed:
          //a staff role without feat.reporting gets NoAccess, and the API's ReportingRead policy
          //refuses the same caller independently.
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: false, isExporter: false }}
              requiredFeature="reporting"
            />
          ),
          children: [
            {
              path: "reports",
              children: [
                {
                  index: true,
                  element: <Reports />,
                },
                {
                  path: ":reportId",
                  element: <ViewReport />,
                }
              ],
            },
            {
              path: "performance",
              children: [
                {
                  index: true,
                  element: <Performance />,
                }
              ],
            },
            {
              path: "stock-check-report",
              children: [
                {
                  index: true,
                  element: <StockCheckReports />,
                },
                {
                  path: ":stockCheckId",
                  element: <ViewStockCheckReport />,
                }
              ],
            },
          ],
        },
        {
          //Profile + Tickets + Help routes for all authenticated users (not feature-gated)
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: true, isExporter: true }}
            />
          ),
          children: [
            {
              // Not feature-gated: everyone has a profile. The CoD signature card on it is gated on
              // the cod scope, and its API refuses anyone without it regardless.
              path: "profile",
              element: <Profile />,
            },
            {
              path: "tickets",
              children: [
                {
                  index: true,
                  element: <Tickets />,
                },
                {
                  path: "new",
                  element: <CreateTicket />,
                },
                {
                  path: ":ticketId",
                  element: <ViewTicket />,
                },
              ],
            },
            {
              path: "help",
              children: [
                {
                  index: true,
                  element: <Help />,
                },
                //Generated from the registry so an article can never exist in the
                //index without a route (or vice versa). Per-article gating lives
                //in HelpArticleRoute, since this block isn't feature-gated.
                ...helpArticles.map((article) => ({
                  path: article.slug,
                  element: <HelpArticleRoute article={article} />,
                })),
              ],
            },
          ],
        },
        {
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: true, isExporter: true }}
              requiredFeature="chat"
            />
          ),
          children: [
            {
              path: "chat",
              element: <Chat />,
            },
          ],
        },
        {
          //Leads
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: false, isExporter: false }}
              requiredFeature="leads"
            />
          ),
          children: [
            {
              path: "collections-board",
              element: <CollectionsBoard />,
            },
            {
              path: "leads",
              children: [
                {
                  index: true,
                  element: <Leads />,
                },
                {
                  path: "create",
                  element: <CreateLead />,
                },
                {
                  path: "create-multiple",
                  element: <CreateLeadMultipleVehicles />,
                },
                {
                  path: "deleted",
                  element: <DeletedLeads />,
                },
                {
                  path: ":leadId",
                  element: <Lead />,
                  children: [
                    {
                      path: ":tab",
                      element: <Lead />,
                    },
                  ],
                },
              ],
            },
            {
              path: "bookings",
              children: [
                {
                  index: true,
                  element: <Leads />,
                },
                {
                  path: ":leadId",
                  element: <Lead />,
                  children: [
                    {
                      path: ":tab",
                      element: <Lead />,
                    },
                  ],
                },
                {
                  path: "create",
                  element: <CreateLead />,
                },
                {
                  path: "create-multiple",
                  element: <CreateLeadMultipleVehicles />,
                },
              ],
            },
            {
              path: "quotes",
              element: <Quotes />,
            },
            {
              path: "vehicles-collected",
              element: <VehiclesCollected />,
            },
            {
              path: "waste-tracking",
              children: [
                {
                  index: true,
                  element: <WasteTracking />,
                },
                {
                  path: "hauliers",
                  element: <Hauliers />,
                },
              ],
            },
            {
              path: "calendar",
              element: <Calendar />,
            },
            {
              path: "trip-manager",
              element: <TripManager />,
            },
            {
              path: "old-trip-manager",
              element: <OldTripManager />,
            },
            {
              path: "payments",
              element: <Payments />,
            },
            {
              path: "schedule",
              element: <Schedule />,
            },
          ],
        },
        {
          // Drop Offs — Leads for standard customers, Ops Management for TME.
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: false, isExporter: false }}
              requiredFeature="leads"
              requiredFeatureTme="opsManagement"
            />
          ),
          children: [
            {
              path: "drop-offs",
              element: <DropOff />,
            },
          ],
        },
        {
          // Vehicles list
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: false, isExporter: false }}
              requiredFeature="vehicles"
            />
          ),
          children: [
            {
              path: "vehicles",
              children: [
                {
                  index: true,
                  element: <VehicleList />,
                },
                {
                  path: "create",
                  element: <CreateVehicle />,
                },
                {
                  path: ":vehicleId",
                  element: <Vehicle />,
                },
                {
                  path: ":vehicleId/edit",
                  element: <EditVehicle />,
                },
              ],
            },
            {
              path: "parts-manager",
              children: [
                {
                  index: true,
                  element: <AwaitingInventory />,
                },
                {
                  path: "vehicle-lookup",
                  element: <PartsVehicleLookup />,
                },
              ],
            },
            {
              path: "jobs",
              children: [
                {
                  index: true,
                  element: <Jobs />,
                },
                {
                  path: ":jobId",
                  element: <Job />,
                },
              ],
            },
          ],
        },
        {
          // Ops Management (TME) — falls back to Vehicles for non-TME.
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: false, isExporter: false }}
              requiredFeature="vehicles"
              requiredFeatureTme="opsManagement"
            />
          ),
          children: [
            {
              path: "production-dashboard",
              element: <ProductionDashboard />,
            },
            {
              path: "vehicle-board",
              element: <VehicleBoard />,
            },
            {
              path: "production-planner",
              children: [
                {
                  index: true,
                  element: <ProductionPlanner />,
                },
              ],
            },
          ],
        },
        {
          // Dismantling (TME) — falls back to Vehicles for non-TME.
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: false, isExporter: false }}
              requiredFeature="vehicles"
              requiredFeatureTme="dismantling"
            />
          ),
          children: [
            {
              path: "station-manager",
              element: <StationManager />,
            },
            {
              path: "depollution",
              children: [
                {
                  index: true,
                  element: <DepollutionList />,
                },
                {
                  path: ":depollutionId",
                  element: <Depollution />,
                },
              ],
            },
            {
              path: "triage",
              children: [
                {
                  index: true,
                  element: <TriageList />,
                },
                {
                  path: ":triageId",
                  element: <Triage />,
                },
              ],
            },
          ],
        },
        {
          //Parts
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: false, isExporter: false }}
              requiredFeature="parts"
            />
          ),
          children: [
            {
              path: "work-orders",
              element: <WorkOrders />,
            },
            {
              path: "awaiting-arrival",
              element: <VehiclesAwaitingArrival />,
            },
            {
              path: "parts-checker",
              element: <PartsChecker />,
            },
            {
              path: "stock",
              children: [
                {
                  index: true,
                  element: <Stock />,
                },
                {
                  path: ":partId",
                  element: <ViewStock />,
                },
              ],
            },
            {
              path: "core",
              children: [
                {
                  index: true,
                  element: <Core />,
                },
                {
                  path: ":partId",
                  element: <ViewStock />,
                },
              ],
            },
            // A sibling of core rather than a child: the sidebar's NavLinks have no `end`, so
            // /core/bins would light up the RS Core item as well as this one.
            {
              path: "core-bins",
              element: <CoreBins />,
            },
            {
              path: "stock-qc",
              children: [
                {
                  index: true,
                  element: <StockQc />,
                },
              ]
            },
            {
              path: "part-requests",
              children: [
                {
                  index: true,
                  element: <PartRequests />,
                },
                {
                  path: "new",
                  element: <CreatePartRequest />,
                },
                {
                  path: ":partRequestId",
                  element: <ViewPartRequest />,
                },
              ],
            },
            {
              path: "bids",
              children: [
                {
                  index: true,
                  element: <PartProBids />,
                },
                {
                  path: ":vehicleId",
                  element: <PartProBidVehicle />,
                },
              ],
            },
            // The listing editor is now a tab on the stock page. These live under the
            // parts feature (same as their redirect target) so users without Order
            // Management still land on the stock page rather than a NoAccess screen.
            {
              path: "ebay-listings/:partId",
              element: <ListingRedirect />,
            },
            {
              path: "ebay-unlisted/:partId",
              element: <ListingRedirect />,
            },
            {
              path: "recently-listed/:partId",
              element: <ListingRedirect />,
            },
            {
              path: "ebay-optimisation/:partId",
              element: <ListingRedirect />,
            },
          ],
        },
        {
          //Order Management
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: false, isExporter: false }}
              requiredFeature="orderManagement"
            />
          ),
          children: [
            {
              path: "ebay-listings",
              children: [
                {
                  index: true,
                  element: <EbayListings />,
                },
                {
                  path: "bulk-list/:batchId",
                  element: <BulkList />,
                },
              ],
            },
            {
              path: "ebay-optimisation",
              element: <EbayOptimisation />,
            },
            {
              path: "ebay-unlisted",
              children: [
                {
                  index: true,
                  element: <EbayUnlisted />,
                },
              ],
            },
            {
              path: "recently-listed",
              children: [
                {
                  index: true,
                  element: <RecentlyListed />,
                },
              ],
            },
            {
              path: "ebay-orders",
              children: [
                {
                  index: true,
                  element: <EbayOrders />,
                },
              ],
            },
            {
              path: "ebay-returns",
              children: [
                {
                  index: true,
                  element: <EbayReturns />,
                },
              ],
            },
          ],
        },
        {
          //Accounts
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: false, isExporter: false }}
              requiredFeature="accounts"
            />
          ),
          children: [
            {
              path: "contacts",
              children: [
                {
                  index: true,
                  element: <ContactList />,
                },
                {
                  path: ":contactId",
                  element: <Contact />,
                },
              ],
            },
            {
              path: "accounts",
              children: [
                {
                  index: true,
                  element: <AccountList />
                },
                {
                  // Merging and tidying are admin-only on the API too.
                  element: (
                    <RequireAuth
                      allowedRoles={{ isAdmin: true, isStaff: false, isMechanic: false, isDriver: false, isExporter: false }}
                      requiredFeature="accounts"
                    />
                  ),
                  children: [{ path: "duplicates", element: <AccountDuplicates /> }],
                },
                {
                  path: ':accountId',
                  element: <Account />
                }
              ]
            },
            {
              path: "invoices",
              children: [
                {
                  index: true,
                  element: <Invoices />
                },
                {
                  path: ":invoiceId",
                  element: <ViewInvoice />
                }
              ]
            },
            {
              path: "sales-quotes",
              children: [
                {
                  index: true,
                  element: <SalesQuotes />
                },
                {
                  path: ":salesQuoteId",
                  element: <ViewSalesQuote />
                }
              ]
            },
          ],
        },
        {
          //Admin-scoped extras that live under staff role checks historically
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: false, isExporter: false }}
              requiredFeature="admin"
            />
          ),
          children: [
            {
              path: "driver-tracking",
              element: <DriverTracking />,
            },
            {
              path: "settings",
              element: <Settings />,
              children: [
                {
                  index: true,
                  element: <GeneralSettings />,
                },
                {
                  path: "mail-importer",
                  element: <MailSettings />,
                },
              ],
            },
          ],
        },
        {
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: false, isExporter: false }}
              requiredFeature="dashboard"
            />
          ),
          children: [
            {
              path: "search",
              element: <Search />,
            },
          ],
        },
        {
          //Widget
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: false, isExporter: false }}
              requiredFeature="widget"
            />
          ),
          children: [
            {
              path: "widget-dashboard",
              element: <IFrame src={widgetPath("")} />,
            },
            {
              // Widget leads keep their widget id when they land in Core, so the id carries across.
              path: "widget-dashboard/leads/:leadId",
              element: <WidgetLeadIFrame />,
            },
            {
              path: "widget-calculator",
              element: <IFrame src={widgetPath("calculate")} />,
            },
            {
              path: "widget-postcodes",
              element: <IFrame src={widgetPath("scrap-price")} />,
            },
            {
              path: "widget-suppliers",
              element: <IFrame src={widgetPath("suppliers")} />,
            },
            {
              path: "sellavehicle",
              element: <IFrame src={widgetPath("sellavehicle")} />,
            },
            {
              path: "widget-config",
              element: <IFrame src={widgetPath("price-settings")} />,
            },
            {
              path: "abandoned-quotes",
              element: <IFrame src={widgetPath("abandoned-quotes")} />,
            },
          ],
        },
        {
          //Driver routes
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: false, isMechanic: false, isDriver: true, isExporter: false }}
              requiredFeature="findLead"
            />
          ),
          children: [
            {
              path: "find-lead",
              element: <FindLead />,
            },
            {
              path: "/driver/lead/:leadId",
              element: <DriverLead />,
            },
          ],
        },
        {
          //Exporter / exports (parts scope)
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: true, isExporter: true }}
              requiredFeature="parts"
            />
          ),
          children: [
            {
              path: "exports",
              element: <Exports />,
            }
          ]
        }
      ],
    },
  ],
  {
    basename: process.env.PUBLIC_URL,
  }
);

registerSameTabNavigate((path) => router.navigate(path));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      staleTime: 1000 * 60 * 2, //2 minutes
    },
  },
});


//Moved some of the providers to layouts to separate public and private routes

root.render(
  <QueryClientProvider client={queryClient}>
    <NotifierContextProvider
      config={{
        max: 5,
        position: "topRight",
      }}
    >
      <DndProvider backend={HTML5Backend}>
        <RouterProvider router={router} />
        <ReactQueryDevtools initialIsOpen={false} />
      </DndProvider>
    </NotifierContextProvider>
  </QueryClientProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();
